import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';

const BaggageStatusChip = ({ status }) => {
  const getChipProps = () => {
    switch (status) {
      case true:
        return {
          color: 'success',
          variant: 'filled',
          label: 'Incluido',
        };
      case false:
        return {
          color: 'error',
          variant: 'filled',
          label: 'No Incluido',
        };
      case 'Pendiente':
        return {
          color: 'warning',
          variant: 'filled',
          label: 'Pendiente',
        };
      default:
        return {
          color: 'default',
          variant: 'filled',
          label: status,
        };
    }
  };

  return <Chip {...getChipProps()} size='small' sx={{ height: '24px', fontSize: '0.75rem' }} />;
};

const BaggageOptions = ({
  services,
  passengerId,
  journeyRef,
  selectedServices,
  onBaggageSelect,
  color,
}) => {
  return (
    <FormGroup sx={{ p: 2, bgcolor: 'grey.50' }}>
      {services.map((service) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedServices?.includes(service.id) || false}
              onChange={(e) =>
                onBaggageSelect(passengerId, journeyRef, service.id, e.target.checked)
              }
              sx={{
                color,
                '&.Mui-checked': {
                  color,
                },
              }}
            />
          }
          key={service.id}
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography variant='body2'>{service.short_name}</Typography>
              <Typography color='text.secondary' variant='body2'>
                {service.currency} {service.total}
              </Typography>
            </Box>
          }
          sx={{
            width: '100%',
            margin: 0,
            '.MuiFormControlLabel-label': {
              width: '100%',
            },
          }}
        />
      ))}
    </FormGroup>
  );
};

const BaggageItem = ({ baggage }) => {
  const getQuantity = () => {
    if (typeof baggage.quantity !== 'undefined') {
      return baggage.quantity;
    }
    return baggage.included ? 1 : 0;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {baggage.category === 'HandBaggage' ? (
        <WorkOutlineIcon color='action' fontSize='small' />
      ) : (
        <LuggageOutlinedIcon color='action' fontSize='small' />
      )}
      <Typography color='text.secondary' sx={{ ml: 0.5 }} variant='caption'>
        x{getQuantity()}
      </Typography>
    </Box>
  );
};

const BaggageRoute = ({
  direction,
  route,
  handBaggage = [],
  checkedBaggage = [],
  color,
  journeyRef,
  services,
  passengerId,
  selectedServices = [],
  onBaggageSelect,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const availableServices = services[journeyRef] || [];
  const hasAvailableServices = availableServices.length > 0;

  const selectedServiceDetails = availableServices.filter((service) =>
    selectedServices.includes(service.id)
  );

  const renderBaggageRow = (baggage, isLast, index) => (
    <Box
      key={index}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: 'grey.50',
        border: '1px solid #ccc',
        borderTop: 'none',
        p: 1.5,
        borderBottomLeftRadius: isLast ? (showOptions ? 0 : 15) : 0,
        borderBottomRightRadius: isLast ? (showOptions ? 0 : 15) : 0,
      }}
    >
      <BaggageItem baggage={baggage} />
      <Typography color={baggage.included ? '#000' : '#ccc'} sx={{ flex: 1 }} variant='body2'>
        {baggage.short_name}
        {baggage.weight !== '0' && ` (${baggage.weight})`}
      </Typography>
      <BaggageStatusChip status={baggage.included} />
    </Box>
  );

  // El botón de añadir equipaje solo se muestra con Tooltip cuando no hay servicios disponibles
  const addBaggageButton = (
    <Button
      disabled={!hasAvailableServices}
      fullWidth
      onClick={() => setShowOptions(!showOptions)}
      startIcon={<AddIcon />}
      sx={{
        color: 'text.secondary',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderRadius: showOptions ? 0 : '0 0 15px 15px',
        py: 1,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&.Mui-disabled': {
          color: 'text.disabled',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {showOptions && hasAvailableServices ? 'Ocultar opciones' : 'Añadir equipaje'}
    </Button>
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          border: '1px solid #ccc',
          borderBottom: 'none',
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
        }}
      >
        <Box component='span' sx={{ display: 'inline-flex', mr: 1, color }}>
          {direction === 'Ida' ? (
            <ArrowForwardOutlinedIcon sx={{ color }} />
          ) : (
            <ArrowBackOutlinedIcon sx={{ color }} />
          )}
        </Box>
        <Typography sx={{ flex: 1 }} variant='body2'>
          {direction} {route}
        </Typography>
      </Box>

      {/* Equipaje de mano */}
      {handBaggage.map((bag, index) =>
        renderBaggageRow(
          bag,
          index === handBaggage.length - 1 &&
            checkedBaggage.length === 0 &&
            selectedServiceDetails.length === 0,
          index
        )
      )}

      {/* Equipaje facturado */}
      {checkedBaggage.map((bag, index) =>
        renderBaggageRow(
          bag,
          index === checkedBaggage.length - 1 && selectedServiceDetails.length === 0,
          index + handBaggage.length
        )
      )}

      {/* Equipaje extra seleccionado */}
      {selectedServiceDetails.map((service, index) => (
        <Box
          key={service.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'grey.50',
            border: '1px solid #ccc',
            borderTop: 'none',
            p: 1.5,
            borderBottomLeftRadius:
              !showOptions && index === selectedServiceDetails.length - 1 ? 15 : 0,
            borderBottomRightRadius:
              !showOptions && index === selectedServiceDetails.length - 1 ? 15 : 0,
          }}
        >
          <BaggageItem baggage={{ category: 'Baggage', quantity: 1 }} />
          <Typography sx={{ flex: 1 }} variant='body2'>
            {service.short_name}
          </Typography>
          <BaggageStatusChip status='Pendiente' />
        </Box>
      ))}

      {/* Opciones de equipaje extra */}
      <Collapse in={showOptions && hasAvailableServices}>
        <BaggageOptions
          color={color}
          journeyRef={journeyRef}
          onBaggageSelect={onBaggageSelect}
          passengerId={passengerId}
          selectedServices={selectedServices}
          services={availableServices}
        />
      </Collapse>

      {/* Botón de añadir equipaje con o sin Tooltip según disponibilidad de servicios */}
      {hasAvailableServices ? (
        addBaggageButton
      ) : (
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                color: '#000000',
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
              },
            },
          }}
          placement='top'
          title='No hay equipaje extra que añadir'
        >
          <span>{addBaggageButton}</span>
        </Tooltip>
      )}
    </Box>
  );
};

export default BaggageRoute;
